<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-orange-600 rounded-full"></div>
          <span class="text-[10px] font-black text-orange-600 tracking-widest">Recarga de Gas</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2">
          <Icon icon="mdi:propane-tank" class="text-orange-600" />
          Tipos de Recarga
          <span class="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/40 text-orange-600 text-xs font-black rounded-lg">{{ tipos.length }}</span>
        </h1>
      </div>
      <button @click="openCreateModal" class="px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl font-black text-xs tracking-widest shadow-md flex items-center gap-2">
        <Icon icon="mdi:plus-circle" /> Nuevo Tipo
      </button>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-4 mb-4 dark:border-gray-700">
      <div class="relative">
        <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input v-model="searchQuery" placeholder="Buscar por código o nombre..." class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-orange-500 rounded-xl outline-none dark:text-white font-bold text-sm" />
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-16">
      <Icon icon="mdi:loading" class="animate-spin text-orange-600" width="40" />
    </div>

    <div v-else-if="filteredTipos.length === 0" class="text-center py-16 opacity-40">
      <Icon icon="mdi:fire-off" class="text-6xl mb-3 mx-auto" />
      <p class="text-lg font-black">No hay tipos de recarga</p>
    </div>

    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div v-for="t in filteredTipos" :key="t.idTipoRecarga" class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all">
        <div class="bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:fire" class="text-white" />
            <span class="text-white font-black text-sm">{{ t.codigo }}</span>
          </div>
          <span class="px-2 py-0.5 bg-white/20 text-white text-[10px] font-black rounded">{{ t.estado }}</span>
        </div>
        <div class="p-4">
          <h3 class="text-base font-black text-gray-900 dark:text-white mb-3">{{ t.nombre }}</h3>
          <div class="grid grid-cols-2 gap-3 mb-4">
            <div class="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-3 text-center">
              <p class="text-[10px] font-black text-orange-500 uppercase">Kilos</p>
              <p class="text-xl font-black text-orange-600">{{ t.kilogramos }} kg</p>
            </div>
            <div class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-center">
              <p class="text-[10px] font-black text-blue-500 uppercase">Litros</p>
              <p class="text-xl font-black text-blue-600">{{ t.litrosEquivalente }} L</p>
            </div>
          </div>
          <div class="space-y-2 mb-4">
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 font-bold">Precio USD:</span>
              <span class="font-black text-emerald-600">${{ formatearMoneda(t.precioUsd) }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-500 font-bold">Precio Bs:</span>
              <span class="font-black text-blue-600">Bs. {{ formatearMoneda(t.precioBs) }}</span>
            </div>
            <div v-if="t.margenGanancia" class="flex justify-between text-sm">
              <span class="text-gray-500 font-bold">Margen:</span>
              <span class="font-black text-purple-600">{{ t.margenGanancia }}%</span>
            </div>
          </div>
          <div class="flex gap-2 pt-3 border-t dark:border-gray-700">
            <button @click="openEditModal(t)" class="flex-1 py-2 bg-amber-50 text-amber-600 rounded-lg font-black text-[10px] flex items-center justify-center gap-1">
              <Icon icon="mdi:pencil" /> Editar
            </button>
            <button @click="abrirModalEliminar(t)" class="flex-1 py-2 bg-rose-50 text-rose-600 rounded-lg font-black text-[10px] flex items-center justify-center gap-1">
              <Icon icon="mdi:delete" /> Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Formulario -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="showModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          <div class="bg-gradient-to-r from-orange-600 to-amber-600 px-6 py-4 flex items-center gap-3">
            <Icon :icon="editing ? 'mdi:pencil-box' : 'mdi:fire'" class="text-white text-xl" />
            <h3 class="text-sm font-black text-white">{{ editing ? 'Editar Tipo de Recarga' : 'Nuevo Tipo de Recarga' }}</h3>
            <button @click="closeModal" class="ml-auto p-1.5 bg-white/10 rounded-lg text-white"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <form @submit.prevent="save" class="space-y-4">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Código *</label>
                  <input v-model="form.codigo" required placeholder="GAS-10" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-orange-500 rounded-xl text-xs dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Nombre *</label>
                  <input v-model="form.nombre" required placeholder="Recarga 10 kg" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-xs dark:text-white outline-none" />
                </div>
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Descripción</label>
                <textarea v-model="form.descripcion" rows="2" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-xs dark:text-white outline-none resize-none"></textarea>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Kilogramos *</label>
                  <input v-model.number="form.kilogramos" type="number" step="0.01" required @input="recalcularLitros" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-xs dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Litros Equivalente *</label>
                  <input v-model.number="form.litrosEquivalente" type="number" step="0.01" required class="w-full px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-100 dark:border-blue-800 rounded-xl text-xs dark:text-white outline-none" />
                  <p class="text-[9px] text-blue-600 mt-1">Se calcula automático (1kg ≈ 1.96L), pero puedes editarlo</p>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Precio USD *</label>
                  <input 
                    v-model.number="form.precioUsd" 
                    type="number" 
                    step="0.01" 
                    required 
                    @input="calcularPrecioBs"
                    class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-orange-500 rounded-xl text-xs dark:text-white outline-none" 
                  />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 flex items-center gap-1">
                    Precio Bs
                    <span class="text-emerald-600 text-[9px]">(Auto BCV)</span>
                  </label>
                  <input 
                    :value="precioBsFormateado" 
                    type="text" 
                    readonly
                    class="w-full px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-100 dark:border-emerald-800 rounded-xl text-xs font-black text-emerald-700 dark:text-emerald-300 outline-none cursor-not-allowed" 
                    placeholder="Se calcula automático" 
                  />
                  <p v-if="tasaActual > 0" class="text-[9px] text-emerald-600 mt-1">
                    💱 Tasa BCV: {{ tasaActual.toFixed(2) }} Bs/USD
                  </p>
                  <p v-else class="text-[9px] text-amber-600 mt-1">
                    ⚠️ No hay tasa BCV disponible
                  </p>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Costo USD</label>
                  <input v-model.number="form.costoUsd" type="number" step="0.01" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-xs dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Margen %</label>
                  <input v-model.number="form.margenGanancia" type="number" step="0.01" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-xs dark:text-white outline-none" />
                </div>
              </div>
              <div class="flex gap-4">
                <label class="flex items-center gap-2 text-xs font-bold dark:text-gray-300"><input type="checkbox" v-model="form.esEditable" class="w-4 h-4" /> Editable</label>
                <label class="flex items-center gap-2 text-xs font-bold dark:text-gray-300"><input type="checkbox" v-model="form.requiereDelivery" class="w-4 h-4" /> Requiere delivery</label>
              </div>
              <div class="flex gap-3 pt-4 border-t dark:border-gray-700">
                <button type="button" @click="closeModal" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl font-black text-xs dark:text-gray-300">Cancelar</button>
                <button type="submit" :disabled="saving" class="flex-[2] py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" />
                  {{ saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal Confirmación Eliminar -->
    <Teleport to="body">
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" @click.self="showDeleteModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div class="bg-gradient-to-r from-red-600 to-rose-600 px-6 py-4 flex items-center gap-3">
            <Icon icon="mdi:alert-circle" class="text-white text-xl" />
            <h3 class="text-sm font-black text-white">Confirmar Eliminación</h3>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">
              ¿Estás seguro de eliminar <strong class="text-red-600">{{ tipoAEliminar?.nombre }}</strong>?
            </p>
            <p class="text-xs text-gray-500 mt-2">Esta acción no se puede deshacer.</p>
          </div>
          <div class="px-6 py-4 border-t dark:border-gray-700 flex gap-3">
            <button @click="showDeleteModal = false" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl font-black text-xs dark:text-gray-300">Cancelar</button>
            <button @click="confirmarEliminar" :disabled="saving" class="flex-[2] py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50">
              {{ saving ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";   // ⭐ AGREGADO
import { 
  obtenerTiposRecarga, 
  crearTipoRecarga, 
  actualizarTipoRecarga, 
  eliminarTipoRecarga, 
  convertirKilosALitros 
} from '@/api/gas.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';

export default {
  name: 'TiposRecargaList',
  components: { Icon },
  setup() {                                        // ⭐ AGREGADO
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      tipos: [],
      loading: false,
      saving: false,
      showModal: false,
      showDeleteModal: false,
      editing: null,
      tipoAEliminar: null,
      searchQuery: '',
      tasaActual: 0,
      form: {
        codigo: '',
        nombre: '',
        descripcion: '',
        kilogramos: 0,
        litrosEquivalente: 0,
        precioUsd: 0,
        precioBs: 0,
        costoUsd: 0,
        margenGanancia: 0,
        esEditable: true,
        requiereDelivery: true,
        estado: 'activo'
      }
    };
  },
  computed: {
    filteredTipos() {
      if (!this.searchQuery || !this.searchQuery.trim()) return this.tipos;
      const q = this.searchQuery.toLowerCase();
      return this.tipos.filter(t =>
        (t.codigo || '').toLowerCase().includes(q) ||
        (t.nombre || '').toLowerCase().includes(q)
      );
    },
    precioBsFormateado() {
      if (!this.form.precioBs || this.form.precioBs <= 0) return '';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(this.form.precioBs);
    }
  },
  async mounted() {
    await this.cargarTasaActual();
    await this.load();
  },
  methods: {
    async cargarTasaActual() {
      try {
        const res = await obtenerTasaVentaActual();
        if (res && res.success && res.data) {
          this.tasaActual = Number(res.data.precioUsdBs || res.data.precio_usd_bs || 0);
        }
      } catch (error) {
        console.error('❌ Error cargando tasa:', error);
        this.tasaActual = 0;
      }
    },

    async load() {
      this.loading = true;
      this.tipos = [];
      try {
        const res = await obtenerTiposRecarga();
        if (!res || typeof res !== 'object') {
          this.toast.error('No se recibió respuesta válida');
          return;
        }
        if (res.success === true) {
          this.tipos = Array.isArray(res.data) ? res.data : [];
        } else {
          this.toast.error(res.error || 'Error al cargar tipos');
        }
      } catch (error) {
        console.error('💥 Excepción en load:', error);
        this.toast.error(error?.message || 'Error de conexión');
      } finally {
        this.loading = false;
      }
    },

    async recalcularLitros() {
      try {
        if (this.form.kilogramos > 0) {
          const res = await convertirKilosALitros(this.form.kilogramos);
          if (res && res.success && res.data) {
            this.form.litrosEquivalente = res.data.litros;
          }
        }
      } catch (error) {
        console.error('❌ Error convirtiendo:', error);
      }
    },

    calcularPrecioBs() {
      if (!this.form.precioUsd || !this.tasaActual || this.tasaActual <= 0) {
        if (!this.form.precioUsd) this.form.precioBs = 0;
        return;
      }
      this.form.precioBs = parseFloat((this.form.precioUsd * this.tasaActual).toFixed(2));
    },

    formatearMoneda(valor) {
      if (valor === null || valor === undefined || valor === '') return '0.00';
      const num = Number(valor);
      if (isNaN(num)) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);
    },

    async openCreateModal() {
      await this.cargarTasaActual();
      this.editing = null;
      this.form = {
        codigo: '',
        nombre: '',
        descripcion: '',
        kilogramos: 0,
        litrosEquivalente: 0,
        precioUsd: 0,
        precioBs: 0,
        costoUsd: 0,
        margenGanancia: 0,
        esEditable: true,
        requiereDelivery: true,
        estado: 'activo'
      };
      this.showModal = true;
    },

    async openEditModal(t) {
      await this.cargarTasaActual();
      this.editing = t;
      this.form = {
        codigo: t.codigo || '',
        nombre: t.nombre || '',
        descripcion: t.descripcion || '',
        kilogramos: t.kilogramos || 0,
        litrosEquivalente: t.litrosEquivalente || 0,
        precioUsd: t.precioUsd || 0,
        precioBs: t.precioBs || 0,
        costoUsd: t.costoUsd || 0,
        margenGanancia: t.margenGanancia || 0,
        esEditable: t.esEditable !== false,
        requiereDelivery: t.requiereDelivery !== false,
        estado: t.estado || 'activo'
      };
      if (this.form.precioUsd > 0 && (!this.form.precioBs || this.form.precioBs === 0)) {
        this.calcularPrecioBs();
      }
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async save() {
      if (!this.form.codigo || !this.form.codigo.trim()) {
        this.toast.warning('El código es obligatorio');
        return;
      }
      if (!this.form.nombre || !this.form.nombre.trim()) {
        this.toast.warning('El nombre es obligatorio');
        return;
      }
      if (!this.form.kilogramos || this.form.kilogramos <= 0) {
        this.toast.warning('Los kilogramos deben ser mayor a 0');
        return;
      }
      if (!this.form.precioUsd || this.form.precioUsd <= 0) {
        this.toast.warning('El precio USD debe ser mayor a 0');
        return;
      }

      this.saving = true;

      try {
        let res = null;

        if (this.editing) {
          res = await actualizarTipoRecarga(this.editing.idTipoRecarga, this.form);
        } else {
          res = await crearTipoRecarga(this.form);
        }

        if (!res || typeof res !== 'object') {
          this.toast.error('No se recibió respuesta válida del servidor');
          return;
        }

        if (res.success === true) {
          this.toast.success(this.editing ? 'Tipo actualizado correctamente' : 'Tipo creado correctamente');
          await this.load();
          this.closeModal();
        } else {
          this.toast.error(res.error || res.message || 'Error al guardar');
        }
      } catch (error) {
        console.error('💥 Excepción en save:', error);
        this.toast.error(error?.message || 'Error inesperado');
      } finally {
        this.saving = false;
      }
    },

    abrirModalEliminar(tipo) {
      this.tipoAEliminar = tipo;
      this.showDeleteModal = true;
    },

    async confirmarEliminar() {
      if (!this.tipoAEliminar) return;
      this.saving = true;
      try {
        const res = await eliminarTipoRecarga(this.tipoAEliminar.idTipoRecarga);
        if (!res || typeof res !== 'object') {
          this.toast.error('No se recibió respuesta válida');
          return;
        }
        if (res.success === true) {
          this.toast.success('Tipo eliminado correctamente');
          await this.load();
          this.showDeleteModal = false;
          this.tipoAEliminar = null;
        } else {
          this.toast.error(res.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('💥 Excepción en confirmarEliminar:', error);
        this.toast.error(error?.message || 'Error de conexión');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
</style>